import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { AlertTriangle, Pencil, Plus, PowerOff, RotateCcw } from 'lucide-react';
import { UnitType } from '@/lib/shared-types';
import { api } from '@/lib/api';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { ErrorState, LoadingState } from '@/components/ui/states';
import { Table, TD, TH, THead, TR, TableEmpty, TableWrap } from '@/components/ui/table';
import { apiMessage } from '../shared';
import { ToggleRow } from '@/components/ui/switch';
import {
  EMPTY_UNIT,
  FACTOR_SCALE,
  baseUnitOptions,
  foldToRoot,
  isolatedFrom,
  unitProblem,
  type UnitDraft,
  type UnitRow,
} from './inventoryDefs';

export type UnitTarget = UnitRow | 'new';

const UNIT_TYPES: UnitType[] = Object.values(UnitType);

interface PanelProps {
  units: UnitRow[];
  loading: boolean;
  error: string | null;
  onRetry: () => void;
  mayWrite: boolean;
  onEdit: (target: UnitTarget) => void;
  onChanged: () => void;
}

/**
 * X-16 — units of measure.
 *
 * A unit is not decoration: it is what every quantity on the stock ledger is
 * stored in and what every recipe line is converted through. Until now the
 * tenant was stuck with whatever the seed left, so an item measured in
 * anything else simply could not be defined.
 */
export function UnitsPanel({
  units,
  loading,
  error,
  onRetry,
  mayWrite,
  onEdit,
  onChanged,
}: PanelProps) {
  const { t } = useTranslation();
  const { confirm } = useConfirm();
  const [busyId, setBusyId] = useState<string | null>(null);

  const byId = useMemo(() => new Map(units.map((unit) => [unit.id, unit])), [units]);

  /**
   * `DELETE /inventory/units/:id` writes `isActive: false` and nothing more —
   * `UnitsService.remove` does not look for items, recipes or movements that
   * use it, and there is no endpoint anywhere that answers "which recipes use
   * this unit" (`QueryRecipeDto` filters by catalog item and active flag only,
   * and `GET /recipes` does not return components). So this asks with the count
   * it CAN establish exactly — the inventory items stocked in it, straight from
   * `meta.total` — and is explicit that recipes are not part of that count
   * rather than implying the number is complete.
   */
  const retire = async (unit: UnitRow) => {
    let itemsUsing: number | null = null;
    try {
      const { data } = await api.get('/inventory/items', {
        params: { unitId: unit.id, limit: 1 },
      });
      itemsUsing = (data as { meta?: { total?: number } })?.meta?.total ?? null;
    } catch {
      itemsUsing = null;
    }
    const derived = units.filter((row) => row.baseUnitId === unit.id).length;

    const ok = await confirm({
      title: t('operations.inventory.defs.units.retireTitle', { code: unit.code }),
      body: t('operations.inventory.defs.units.retireBody', {
        items: itemsUsing ?? 0,
        derived,
      }),
      confirmLabel: t('operations.inventory.defs.units.retire'),
      tone: 'danger',
    });
    if (!ok) return;

    setBusyId(unit.id);
    try {
      await api.delete(`/inventory/units/${unit.id}`);
      toast.success(t('operations.inventory.defs.units.retired', { code: unit.code }));
      onChanged();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.inventory.defs.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  const reactivate = async (unit: UnitRow) => {
    setBusyId(unit.id);
    try {
      await api.patch(`/inventory/units/${unit.id}`, { isActive: true });
      toast.success(t('operations.inventory.defs.units.reactivated', { code: unit.code }));
      onChanged();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.inventory.defs.saveFailed')));
    } finally {
      setBusyId(null);
    }
  };

  if (error) return <ErrorState message={error} onRetry={onRetry} />;
  if (loading && !units.length) return <LoadingState label={t('common.loading')} />;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-ink-muted">
          {t('operations.inventory.defs.units.summary', { count: units.length })}
        </p>
        {mayWrite && (
          <Button onClick={() => onEdit('new')}>
            <Plus className="h-4 w-4" />
            {t('operations.inventory.defs.units.new')}
          </Button>
        )}
      </div>

      <TableWrap>
        <Table>
          <THead>
            <TR>
              <TH>{t('operations.inventory.defs.units.code')}</TH>
              <TH>{t('operations.inventory.defs.units.name')}</TH>
              <TH>{t('operations.inventory.defs.units.type')}</TH>
              <TH>{t('operations.inventory.defs.units.converts')}</TH>
              <TH>{t('common.status')}</TH>
              {mayWrite && <TH className="text-end">{t('operations.common.actions')}</TH>}
            </TR>
          </THead>
          <tbody>
            {!units.length ? (
              <TableEmpty colSpan={mayWrite ? 6 : 5}>
                {t('operations.inventory.defs.units.none')}
              </TableEmpty>
            ) : (
              units.map((unit) => {
                const base = unit.baseUnitId ? byId.get(unit.baseUnitId) : null;
                return (
                  <TR key={unit.id}>
                    <TD className="font-mono font-medium">{unit.code}</TD>
                    <TD>{unit.name}</TD>
                    <TD>{t(`operations.inventory.defs.unitTypes.${unit.type}`)}</TD>
                    <TD>
                      {unit.baseUnitId ? (
                        <span dir="ltr" className="tabular-nums">
                          1 {unit.code} = {trimFactor(unit.factorToBase)} {base?.code ?? '?'}
                        </span>
                      ) : (
                        <Badge tone="info">
                          {t('operations.inventory.defs.units.baseUnit')}
                        </Badge>
                      )}
                    </TD>
                    <TD>
                      <Badge tone={unit.isActive ? 'neutral' : 'danger'}>
                        {t(
                          unit.isActive
                            ? 'operations.inventory.defs.active'
                            : 'operations.inventory.defs.inactive',
                        )}
                      </Badge>
                    </TD>
                    {mayWrite && (
                      <TD className="text-end">
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="outline"
                            size="icon"
                            aria-label={t('common.edit')}
                            title={t('common.edit')}
                            disabled={busyId === unit.id}
                            onClick={() => onEdit(unit)}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {unit.isActive ? (
                            <Button
                              variant="outline"
                              size="icon"
                              aria-label={t('operations.inventory.defs.units.retire')}
                              title={t('operations.inventory.defs.units.retire')}
                              disabled={busyId === unit.id}
                              onClick={() => retire(unit)}
                            >
                              <PowerOff className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button
                              size="icon"
                              aria-label={t('operations.inventory.defs.units.reactivate')}
                              title={t('operations.inventory.defs.units.reactivate')}
                              disabled={busyId === unit.id}
                              onClick={() => reactivate(unit)}
                            >
                              <RotateCcw className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </TD>
                    )}
                  </TR>
                );
              })
            )}
          </tbody>
        </Table>
      </TableWrap>
    </div>
  );
}

/** `Decimal(18,6)` comes back as `1000.000000`; a factor reads better without. */
function trimFactor(value: string): string {
  return value.includes('.') ? value.replace(/0+$/, '').replace(/\.$/, '') : value;
}

interface DialogProps {
  target: UnitTarget | null;
  units: UnitRow[];
  onClose: () => void;
  onSaved: () => void;
}

export function UnitDialog({ target, units, onClose, onSaved }: DialogProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<UnitDraft>(EMPTY_UNIT);
  const [busy, setBusy] = useState(false);
  const editing = target && target !== 'new' ? target : null;

  useEffect(() => {
    if (!target) return;
    setDraft(
      target === 'new'
        ? EMPTY_UNIT
        : {
            code: target.code,
            name: target.name,
            type: target.type,
            baseUnitId: target.baseUnitId ?? '',
            factorToBase: trimFactor(target.factorToBase),
            isActive: target.isActive,
          },
    );
  }, [target]);

  const bases = useMemo(
    () => baseUnitOptions(units, draft.type, editing?.id ?? null),
    [units, draft.type, editing],
  );

  /**
   * The isolation the form exists to prevent — a second root for a type that
   * already has one, which `convertWith` can never bridge.
   */
  const isolated = useMemo(
    () => isolatedFrom(units, draft, editing?.id ?? null),
    [units, draft, editing],
  );

  const problem = useMemo(() => unitProblem(draft), [draft]);

  /** What the chain will actually mean, folded the way `rootOf()` folds it. */
  const preview = useMemo(() => {
    if (!draft.baseUnitId || !draft.factorToBase.trim() || problem) return null;
    return foldToRoot(units, draft.baseUnitId, draft.factorToBase);
  }, [units, draft, problem]);

  const submit = async () => {
    if (problem) return;
    // `forbidNonWhitelisted` — a blank string is not an omission. A unit with no
    // base must send neither `baseUnitId` nor `factorToBase`; `UnitsService`
    // pins a root's factor at 1 itself.
    const body: Record<string, unknown> = {
      code: draft.code.trim(),
      name: draft.name.trim(),
      type: draft.type,
      isActive: draft.isActive,
      ...(draft.baseUnitId
        ? { baseUnitId: draft.baseUnitId, factorToBase: draft.factorToBase.trim() }
        : {}),
    };
    setBusy(true);
    try {
      if (editing) await api.patch(`/inventory/units/${editing.id}`, body);
      else await api.post('/inventory/units', body);
      toast.success(
        t(
          editing
            ? 'operations.inventory.defs.units.updated'
            : 'operations.inventory.defs.units.created',
          { code: draft.code.trim() },
        ),
      );
      onSaved();
    } catch (e) {
      toast.error(apiMessage(e, t('operations.inventory.defs.saveFailed')));
    } finally {
      setBusy(false);
    }
  };

  const fieldError = (field: 'code' | 'name' | 'factorToBase') =>
    problem?.field === field
      ? t(`operations.inventory.defs.units.errors.${problem.code}`, { scale: FACTOR_SCALE })
      : undefined;

  return (
    <Dialog
      open={!!target}
      onClose={onClose}
      title={t(
        editing ? 'operations.inventory.defs.units.edit' : 'operations.inventory.defs.units.new',
      )}
      description={t('operations.inventory.defs.units.formHint')}
      className="max-w-2xl"
      footer={
        <>
          <Button variant="ghost" size="lg" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button size="lg" onClick={submit} disabled={busy || !!problem}>
            {t('common.save')}
          </Button>
        </>
      }
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label={t('operations.inventory.defs.units.code')} required error={fieldError('code')}>
          <Input
            autoFocus
            className="h-12 font-mono"
            value={draft.code}
            onChange={(e) => setDraft({ ...draft, code: e.target.value })}
            placeholder="KG"
          />
        </Field>

        <Field label={t('operations.inventory.defs.units.name')} required error={fieldError('name')}>
          <Input
            className="h-12"
            value={draft.name}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
            placeholder={t('operations.inventory.defs.units.namePlaceholder')}
          />
        </Field>

        {/*
          Changing the type has to clear the base: `assertConvertible` refuses a
          base of another type on the way in, but `UnitsService.update` only
          runs that check when the PATCH *carries* a `baseUnitId` — so a type
          changed on its own would leave a WEIGHT unit converting into a VOLUME
          one, and nothing would notice until a conversion threw.
        */}
        <Field
          label={t('operations.inventory.defs.units.type')}
          hint={t('operations.inventory.defs.units.typeHint')}
        >
          <Select
            className="h-12"
            value={draft.type}
            onChange={(e) =>
              setDraft({ ...draft, type: e.target.value as UnitType, baseUnitId: '' })
            }
          >
            {UNIT_TYPES.map((type) => (
              <option key={type} value={type}>
                {t(`operations.inventory.defs.unitTypes.${type}`)}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label={t('operations.inventory.defs.units.convertsInto')}
          hint={t('operations.inventory.defs.units.convertsIntoHint')}
        >
          <Select
            className="h-12"
            value={draft.baseUnitId}
            onChange={(e) => setDraft({ ...draft, baseUnitId: e.target.value })}
          >
            <option value="">{t('operations.inventory.defs.units.noBase')}</option>
            {bases.map((unit) => (
              <option key={unit.id} value={unit.id}>
                {unit.name} ({unit.code})
              </option>
            ))}
          </Select>
        </Field>

        {draft.baseUnitId && (
          <Field
            label={t('operations.inventory.defs.units.factor')}
            required
            error={fieldError('factorToBase')}
            hint={t('operations.inventory.defs.units.factorHint', {
              code: draft.code.trim() || '?',
              base: bases.find((u) => u.id === draft.baseUnitId)?.code ?? '?',
            })}
          >
            <Input
              className="h-12"
              inputMode="decimal"
              dir="ltr"
              value={draft.factorToBase}
              onChange={(e) => setDraft({ ...draft, factorToBase: e.target.value })}
              placeholder="1000"
            />
          </Field>
        )}

        {preview && (
          <div className="rounded-lg border bg-subtle p-3 text-sm sm:col-span-2" dir="ltr">
            <span className="tabular-nums">
              1 {draft.code.trim() || '?'} = {trimFactor(String(preview.factor))}{' '}
              {preview.root.code}
            </span>
          </div>
        )}

        {/*
          Not a validation error — the server accepts this and so does the
          model. It is a decision with a consequence, so it is stated as one
          rather than blocked or hidden.
        */}
        {isolated.length > 0 && (
          <div className="flex gap-3 rounded-lg border border-amber-300 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/50 p-3 sm:col-span-2">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-600" />
            <div className="text-sm text-amber-900 dark:text-amber-200">
              <p className="font-medium">
                {t('operations.inventory.defs.units.isolatedTitle')}
              </p>
              <p className="mt-1">
                {t('operations.inventory.defs.units.isolatedBody', {
                  codes: isolated.map((unit) => unit.code).join(', '),
                })}
              </p>
            </div>
          </div>
        )}

        <div className="sm:col-span-2">
          <ToggleRow
            checked={draft.isActive}
            onChange={(isActive) => setDraft({ ...draft, isActive })}
            label={t('operations.inventory.defs.active')}
            hint={t('operations.inventory.defs.units.isActiveHint')}
          />
        </div>
      </div>
    </Dialog>
  );
}
